import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import ChartToggle from '../../../components/ui/ChartToggle';

const ExecutionMetrics = ({ metrics }) => {
  const [chartType, setChartType] = useState('bar');

  // 🔧 PRODUCTION FIX (2025-10-31): Use snake_case from API (not camelCase)
  const metricCards = [
    {
      title: 'Total Orders',
      value: (metrics?.total_orders || 0)?.toLocaleString(),
      change: '+12%', // TODO: Calculate from historical data
      changeType: 'positive',
      icon: 'FileText',
      description: 'Total'
    },
    {
      title: 'Success Rate',
      value: `${(metrics?.success_rate || 0)?.toFixed(1)}%`,
      change: '+2.1%', // TODO: Calculate from historical data
      changeType: 'positive',
      icon: 'CheckCircle',
      description: 'Last 24h'
    },
    {
      title: 'Avg Execution Time',
      value: `${(metrics?.avg_execution_time_seconds || 0)?.toFixed(2)}s`,
      change: '-0.3s', // TODO: Calculate from historical data
      changeType: 'positive',
      icon: 'Clock',
      description: 'Per trade'
    },
    {
      title: 'Failed Orders',
      value: (metrics?.failed_orders || 0)?.toLocaleString(),
      change: '-8%', // TODO: Calculate from historical data
      changeType: 'positive',
      icon: 'XCircle',
      description: 'Reduced'
    }
  ];

  // 🔧 PRODUCTION FIX (2025-10-31): Use venue breakdown from API
  // If no venue breakdown from API, calculate from metrics or show empty
  const venuePerformance = metrics?.venue_breakdown || [];
  
  // If no venue data, show empty state message
  const hasVenueData = venuePerformance && venuePerformance.length > 0;

  // Chart data for venue performance visualization
  const chartData = venuePerformance?.map((venue) => ({
    name: venue?.venue,
    orders: venue?.orders,
    successRate: venue?.successRate,
    avgTime: venue?.avgTime
  }));

  const chartColors = ['#1E3A8A', '#3B82F6', '#60A5FA', '#93C5FD'];

  // Animation variants for cards
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const renderChart = () => {
    const chartProps = {
      data: chartData,
      width: '100%',
      height: 300
    };

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer {...chartProps}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="orders" 
                stroke="#1E3A8A" 
                strokeWidth={3}
                dot={{ fill: '#1E3A8A', strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, fill: '#3B82F6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer {...chartProps}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="orders" 
                stroke="#1E3A8A" 
                fill="url(#colorGradient)"
                strokeWidth={2}
              />
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        );
      
      default: // bar
        return (
          <ResponsiveContainer {...chartProps}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                }}
              />
              <Bar dataKey="orders" radius={[4, 4, 0, 0]}>
                {chartData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={chartColors?.[index % chartColors?.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <motion.div 
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards?.map((metric, index) => (
          <motion.div 
            key={index} 
            className="bg-card/80 backdrop-blur-sm border border-border rounded-lg p-4 hover:shadow-elevated transition-all duration-300"
            variants={cardVariants}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-between mb-3">
              <motion.div 
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  metric?.changeType === 'positive' ? 'bg-success/10' : 'bg-error/10'
                }`}
                whileHover={{ rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Icon 
                  name={metric?.icon} 
                  size={20} 
                  className={metric?.changeType === 'positive' ? 'text-success' : 'text-error'} 
                />
              </motion.div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                metric?.changeType === 'positive' ?'bg-success/10 text-success' :'bg-error/10 text-error'
              }`}>
                {metric?.change}
              </span>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
              <p className="text-sm text-muted-foreground">{metric?.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{metric?.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Venue Performance with Chart */}
      <motion.div 
        className="bg-card/80 backdrop-blur-sm border border-border rounded-lg"
        variants={cardVariants}
      >
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Venue Performance</h3>
              <p className="text-sm text-muted-foreground">Real-time execution metrics by trading venue</p>
            </div>
            <ChartToggle 
              chartType={chartType} 
              onChartTypeChange={setChartType}
            />
          </div>
        </div>
        
        <div className="p-4">
          {/* Chart Visualization */}
          {hasVenueData ? (
            <motion.div 
              className="mb-6"
              key={chartType}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderChart()}
            </motion.div>
          ) : null}

          {/* Venue Details */}
          {!hasVenueData ? (
            <div className="text-center py-8">
              <Icon name="Database" size={24} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No venue performance data available</p>
              <p className="text-xs text-muted-foreground mt-1">Data will appear as trades are executed</p>
            </div>
          ) : (
            <div className="space-y-4">
              {venuePerformance.map((venue, index) => (
              <motion.div 
                key={index} 
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors duration-200"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.01 }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <span className="text-xs font-bold text-primary">{venue?.venue}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{venue?.venue}</p>
                    <p className="text-xs text-muted-foreground">{venue?.orders} orders today</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6 text-right">
                  <div>
                    <p className="text-sm font-medium text-foreground">{venue?.successRate}%</p>
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{venue?.avgTime}s</p>
                    <p className="text-xs text-muted-foreground">Avg Time</p>
                  </div>
                  <motion.div 
                    className={`w-3 h-3 rounded-full ${
                      venue?.successRate > 98 ? 'bg-success' : 
                      venue?.successRate > 95 ? 'bg-warning' : 'bg-error'
                    }`}
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
              </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
      {/* System Health */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div 
          className="bg-card/80 backdrop-blur-sm border border-border rounded-lg p-4"
          variants={cardVariants}
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-foreground">API Connectivity</h4>
            <div className="flex items-center space-x-1">
              <motion.div 
                className="w-2 h-2 bg-success rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <span className="text-xs text-success">All Connected</span>
            </div>
          </div>
          <div className="space-y-3">
            {['NSE API', 'BSE API', 'Binance API', 'KuCoin API']?.map((api, index) => (
              <motion.div 
                key={index} 
                className="flex items-center justify-between"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-sm text-muted-foreground">{api}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-success">Connected</span>
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          className="bg-card/80 backdrop-blur-sm border border-border rounded-lg p-4"
          variants={cardVariants}
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-foreground">Order Processing</h4>
            <span className="text-xs text-muted-foreground">Last 5 minutes</span>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Queue Length', value: '23 orders' },
              { label: 'Processing Rate', value: '45/min' },
              { label: 'Avg Latency', value: '127ms' },
              { label: 'Error Rate', value: '0.2%', highlight: true }
            ]?.map((item, index) => (
              <motion.div 
                key={index} 
                className="flex items-center justify-between"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-sm text-muted-foreground">{item?.label}</span>
                <span className={`text-sm font-medium ${
                  item?.highlight ? 'text-success' : 'text-foreground'
                }`}>
                  {item?.value}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ExecutionMetrics;